import React from 'react'
import gql from 'graphql-tag'
import memoize from 'lodash.memoize'
import { useApplicationInfo } from './api/use-application-info'
import {
  createVulnerabilitiesGrid,
  createVulnerabilitiesGridContext,
  createVulnerabilitiesGridContextProvider,
  BulkActionProps
} from './grid'
import {
  allAggregationConfigs,
  vulnerabilityOrSinkGroupFragment,
  VulnerabilityOrSinkGroup
} from './shared'
import { SessionMetadataAction } from '../session-metadata'
import { AggregationConfigs, AggregationOptions } from '../aggregations'
import { OrderFieldConfig } from '../../legacy-design-system/order'
import {
  getApplicationOrgId,
  getApplicationDrilldownID,
  getApplicationVulnerabilityDrilldownRoute,
  getApplicationVulnerabilityDrilldownID,
  getApplicationVulnerabilitiesRoute
} from '../route'
import {
  getSeverityColumn,
  getVulnerabilityColumn,
  getProtectStatusColumn,
  getStatusColumn,
  getDateDetectedColumn,
  getRowActionColumn,
  getSessionMetadataColumn,
  getApplicationColumn
} from './grid-columns'
import { createWrappedPagingNav } from '../vulnerability-dashboard'
import { RouteContext, useSessionFiltersSync } from '../route-provider'
import { ChartToggle } from './chart-toggle'
import {
  ChartContainer,
  chartContainerHeight,
  chartHeight,
  TabItemProps
} from './chart-container'
import { renderChartSwitch } from './charts'
import { BulkActionList, FilteredActionList } from './actions/bulk-action-list'
import { getLeastPermissiveRole, getMostPermissiveRole } from '../roles'
import { GQLVulnerability } from '../../graphql/schema/types/vulnerability'
import { GQLVulnerabilityOrSinkGroup } from '../../graphql/schema/types/vulnerability-sink-group'
import { Column } from '../../legacy-design-system/grid'
import { useTotalApplicationCustomAgentSessions } from '../session-metadata/api/total-application-custom-agent-sessions'

export type ChartVisualizationType = 'DISCOVERY' | 'SEVERITY'

const chartVisualizationConfig: Array<TabItemProps<ChartVisualizationType>> = [
  {
    id: 'SEVERITY',
    label: 'Severity'
  },
  {
    id: 'DISCOVERY',
    label: 'Discovery'
  }
]

type OrderField =
  | 'NAME'
  | 'LAST_DETECTED'
  | 'STATUS'
  | 'FIRST_DETECTED'
  | 'SEVERITY'
  | 'APPLICATION'

const orderFieldConfig: OrderFieldConfig<OrderField> = {
  SEVERITY: {
    direction: 'DESC',
    message: { id: 'title_severityLevel' }
  },
  NAME: {
    direction: 'ASC',
    message: { id: 'title_name' }
  },
  LAST_DETECTED: {
    direction: 'DESC',
    message: { id: 'title_vulnerabilities_lastDetected_vulnerability' }
  },
  FIRST_DETECTED: {
    direction: 'DESC',
    message: { id: 'title_firstDetected_vulnerability' }
  },
  STATUS: {
    direction: 'ASC',
    message: { id: 'title_status' }
  },
  APPLICATION: {
    direction: 'ASC',
    message: { id: 'title_application' }
  }
}

type AggregationCategory =
  | 'servers'
  | 'tags'
  | 'severities'
  | 'statuses'
  | 'substatus'
  | 'urls'
  | 'sinks'
  | 'securityStandards'
  | 'types'
  | 'buildNumbers'
  | 'environments'
  | 'modules'
  | 'routes'
  | 'bugtrackerStatuses'
  | 'protectStatuses'

const {
  tags,
  statuses,
  substatus,
  securityStandards,
  severities,
  types,
  urls,
  sinks,
  servers,
  buildNumbers,
  environments,
  modules,
  routes,
  bugtrackerStatuses,
  protectStatuses
} = allAggregationConfigs

const aggregationConfigs: AggregationConfigs<AggregationCategory> = {
  tags,
  statuses,
  substatus,
  securityStandards,
  severities,
  types,
  urls,
  sinks,
  servers,
  buildNumbers,
  environments,
  modules,
  routes,
  bugtrackerStatuses,
  protectStatuses
}

const initialAggregationSelections: AggregationOptions<AggregationCategory> = {
  tags: [],
  statuses: [],
  substatus: [],
  securityStandards: [],
  severities: [],
  urls: [],
  sinks: [],
  servers: [],
  buildNumbers: [],
  environments: [],
  types: [],
  modules: [],
  routes: [],
  bugtrackerStatuses: [],
  protectStatuses: []
}

export const getPageQuery = memoize(
  (id: string) => gql`
    query ApplicationVulnerabilityGrid(
      $offset: Int!
      $limit: Int!
      $organizationID: ID!
      $orderBy: VulnerabilityOrder
      $filterText: String
      $quickFilter: String
      $dateFilter: VulnerabilityDateFilter
      $tags: [String!]
      $statuses: [String!]
      $substatus: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $urls: [String!]
      $sinks: [String!]
      $servers: [String!]
      $buildNumbers: [String!]
      $environments: [String!]
      $types: [String!]
      $modules: [String!]
      $routes: [String!]
      $sessionMetadata: [SessionMetadataFilter]
      $groupedBy: VulnerabilityGroupedByFilter
      $bugtrackerStatuses: [String!]
      $protectStatuses: [String!]
      $agentSessionId: [String]
    ) {
      vulnerabilitiesAndSinkGroups(
        offset: $offset
        limit: $limit
        organizationID: $organizationID
        orderBy: $orderBy
        filters: {
          agentSessionId: $agentSessionId
          applications: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          tags: $tags
          statuses: $statuses
          substatus: $substatus
          securityStandards: $securityStandards
          severities: $severities
          urls: $urls
          sinks: $sinks
          servers: $servers
          buildNumbers: $buildNumbers
          environments: $environments
          types: $types
          modules: $modules
          routes: $routes
          sessionMetadata: $sessionMetadata
          groupedBy: $groupedBy
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatuses: $protectStatuses
        }
      ) {
      items {
        ...VulnerabilityOrSinkGroup
      }
      total
      licensedVulnerabilityCount
    }
  }
  ${vulnerabilityOrSinkGroupFragment}
`
)

const getQuickFiltersQuery = memoize(
  (id) => gql`
    query ApplicationVulnerabilitiesQuickFilters(
      $organizationID: ID!
      $filterText: String
      $dateFilter: VulnerabilityDateFilter
      $tags: [String!]
      $statuses: [String!]
      $substatus: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $urls: [String!]
      $sinks: [String!]
      $servers: [String!]
      $buildNumbers: [String!]
      $environments: [String!]
      $types: [String!]
      $modules: [String!]
      $routes: [String!]
      $sessionMetadata: [SessionMetadataFilter]
      $bugtrackerStatuses: [String!]
      $protectStatuses: [String!]
      $agentSessionId: [String]
    ) {
      vulnerabilityQuickFilters(
        organizationID: $organizationID
        filters: {
          agentSessionId: $agentSessionId
          applications: ["${id}"]
          filterText: $filterText
          dateFilter: $dateFilter
          tags: $tags
          statuses: $statuses
          substatus: $substatus
          securityStandards: $securityStandards
          severities: $severities
          urls: $urls
          sinks: $sinks
          servers: $servers
          buildNumbers: $buildNumbers
          environments: $environments
          types: $types
          modules: $modules
          routes: $routes
          sessionMetadata: $sessionMetadata
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatuses: $protectStatuses
        }
      ) {
        id
        name
        count
      }
    }
  `
)

const getAggregationQuery = (id: string, name: string, queryFragment: string) =>
  gql`
  query ApplicationVulnerabilities${name}Aggregations(
    $organizationID: ID!
    $filterText: String
    $quickFilter: String
    $dateFilter: VulnerabilityDateFilter
    $tags: [String!]
    $statuses: [String!]
    $substatus: [String!]
    $securityStandards: [String!]
    $severities: [String!]
    $urls: [String!]
    $servers: [String!]
    $buildNumbers: [String!]
    $environments: [String!]
    $types: [String!]
    $modules: [String!]
    $routes: [String!]
    $sinks: [String!]
    $sessionMetadata: [SessionMetadataFilter]
    $bugtrackerStatuses: [String!]
    $agentSessionId: [String]
    $protectStatuses: [String!]
  ) {
    vulnerabilityAggregations(
      organizationID: $organizationID
      filters: {
        agentSessionId: $agentSessionId
        applications: ["${id}"]
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        tags: $tags
        statuses: $statuses
        substatus: $substatus
        securityStandards: $securityStandards
        severities: $severities
        urls: $urls
        servers: $servers
        buildNumbers: $buildNumbers
        environments: $environments
        types: $types
        modules: $modules
        routes: $routes
        sinks: $sinks
        sessionMetadata: $sessionMetadata
        bugtrackerStatuses: $bugtrackerStatuses
        protectStatuses: $protectStatuses
      }
    ) {
      ${queryFragment}
    }
  }
`

export const getAggregationQueries = memoize((id) => [
  getAggregationQuery(
    id,
    'Tag',
    `tags {
      count
      key
    }`
  ),
  getAggregationQuery(
    id,
    'Status',
    `statuses {
      count
      key {
        id
        label
        subfiltersId
        subfilters {
          count
          keycode
          label
        }
      }
    }`
  ),
  getAggregationQuery(
    id,
    'BugtrackerStatus',
    `bugtrackerStatuses {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'SecurityStandard',
    `securityStandards {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Severity',
    `severities {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Url',
    `urls {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Type',
    `types {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Environment',
    `environments {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'BuildNumber',
    `buildNumbers {
      count
      key
    }`
  ),
  getAggregationQuery(
    id,
    'Server',
    `servers {
      count
      key {
        id
        name
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Module',
    `modules {
      count
      key {
        id
        name
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Route',
    `routes {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Sink',
    `sinks {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'protectStatuses',
    `protectStatuses {
      count
      key {
        id
        label
      }
    }`
  )
])

const getSessionMetadataFieldsQuery = memoize(
  (id) => gql`
    query ApplicationSessionMetadataFields(
      $organizationID: ID!
    ) {
      sessionMetadataAppFields(
        organizationID: $organizationID
        applicationID: "${id}"
      ) {
        id
        displayLabel
        agentLabel
        count
      }
    }
  `
)

const getSessionMetadataAggregationQuery = memoize(
  (id) => gql`
    query SessionMetadataAggregations(
      $organizationID: ID!
      $filterText: String
      $quickFilter: String
      $dateFilter: VulnerabilityDateFilter
      $tags: [String!]
      $statuses: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $urls: [String!]
      $servers: [String!]
      $buildNumbers: [String!]
      $environments: [String!]
      $types: [String!]
      $modules: [String!]
      $routes: [String!]
      $sessionMetadata: [SessionMetadataFilter]
      $bugtrackerStatuses: [String!]
      $protectStatus: [String!]
      $agentSessionId: [String]
    ) {
      sessionMetadataAggregations(
        organizationID: $organizationID
        applicationID: "${id}"
        filters: {
          agentSessionId: $agentSessionId
          applications: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          tags: $tags
          statuses: $statuses
          securityStandards: $securityStandards
          severities: $severities
          urls: $urls
          servers: $servers
          buildNumbers: $buildNumbers
          environments: $environments
          types: $types
          modules: $modules
          routes: $routes
          sessionMetadata: $sessionMetadata
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatus: $protectStatus
        }
      ) {
        id
        fieldType
        label
        buckets {
          count
          key {
            id
            value
          }
        }
      }
    }
  `
)

const getSeveritySnapshotsQuery = memoize(
  (id) => gql`
    query VulnerabilitySeveritySnapshots(
      $interval: SnapshotIntervalType!
      $organizationID: ID!
      $filterText: String
      $quickFilter: String
      $dateFilter: VulnerabilityDateFilter
      $tags: [String!]
      $statuses: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $urls: [String!]
      $servers: [String!]
      $buildNumbers: [String!]
      $environments: [String!]
      $types: [String!]
      $modules: [String!]
      $routes: [String!]
      $sessionMetadata: [SessionMetadataFilter]
      $bugtrackerStatuses: [String!]
      $agentSessionId: [String]
      $protectStatus: [String!]
    ) {
      vulnerabilitySeveritySnapshots(
        organizationID: $organizationID
        applicationID: "${id}"
        interval: $interval
        filters: {
          agentSessionId: $agentSessionId
          applications: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          tags: $tags
          statuses: $statuses
          securityStandards: $securityStandards
          severities: $severities
          urls: $urls
          servers: $servers
          buildNumbers: $buildNumbers
          environments: $environments
          types: $types
          modules: $modules
          routes: $routes
          sessionMetadata: $sessionMetadata
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatus: $protectStatus
        }
      ) {
        timestamp
        note
        low
        medium
        high
        critical
      }
    }
  `
)

const getDiscoverySnapshotsQuery = memoize(
  (id) => gql`
    query VulnerabilityDiscoverySnapshots(
      $interval: SnapshotIntervalType!
      $organizationID: ID!
      $filterText: String
      $quickFilter: String
      $dateFilter: VulnerabilityDateFilter
      $tags: [String!]
      $statuses: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $urls: [String!]
      $servers: [String!]
      $buildNumbers: [String!]
      $environments: [String!]
      $types: [String!]
      $modules: [String!]
      $routes: [String!]
      $sessionMetadata: [SessionMetadataFilter]
      $bugtrackerStatuses: [String!]
      $agentSessionId: [String]
      $protectStatus: [String!]
    ) {
      vulnerabilityDiscoverySnapshots(
        organizationID: $organizationID
        applicationID: "${id}"
        interval: $interval
        filters: {
          agentSessionId: $agentSessionId
          applications: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          tags: $tags
          statuses: $statuses
          securityStandards: $securityStandards
          severities: $severities
          urls: $urls
          servers: $servers
          buildNumbers: $buildNumbers
          environments: $environments
          types: $types
          modules: $modules
          routes: $routes
          sessionMetadata: $sessionMetadata
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatus: $protectStatus
        }
      ) {
        timestamp
        newlyIntroduced
        duplicate
        remediated
      }
    }
  `
)

export const ApplicationVulnerabiltiesGridContext =
  createVulnerabilitiesGridContext<
    OrderField,
    AggregationCategory,
    ChartVisualizationType
  >({
    initialAggregationSelections,
    initialOrder: 'SEVERITY',
    aggregationConfigs,
    orderFieldConfig
  })

export const getGridPath = ({ organizationID, routeParameters }) => {
  const appId = getApplicationDrilldownID(routeParameters)
  if (!appId) {
    throw new Error('Unable to parse application ID from route')
  }
  return getApplicationVulnerabilitiesRoute(organizationID, appId)
}

export const getDrilldownPath = ({ organizationID, id, routeParameters }) => {
  const appID = getApplicationDrilldownID(routeParameters)

  if (!appID) {
    throw new Error('Unable to parse application ID from route')
  }
  return getApplicationVulnerabilityDrilldownRoute(organizationID, appID, id)
}

export const ApplicationVulnerabiltiesPagingNav = createWrappedPagingNav({
  // @ts-ignore todo TS-3.8.3 - TS-8508
  GridContext: ApplicationVulnerabiltiesGridContext,
  getCurrentIdFromRoute: getApplicationVulnerabilityDrilldownID,
  getGridPath,
  getDrilldownPath
})

const VulnerabilitiesGridMain = createVulnerabilitiesGrid(
  ApplicationVulnerabiltiesGridContext
)

const getQueries = memoize((appID) => ({
  pageQuery: getPageQuery(appID),
  aggregationsQueries: getAggregationQueries(appID),
  quickFiltersQuery: getQuickFiltersQuery(appID),
  sessionMetadataFieldsQuery: getSessionMetadataFieldsQuery(appID),
  sessionMetadataAggregationQuery: getSessionMetadataAggregationQuery(appID),
  severitySnapshotsQuery: getSeveritySnapshotsQuery(appID),
  discoverySnapshotsQuery: getDiscoverySnapshotsQuery(appID)
}))

export const ApplicationVulnerabilitiesGridContextProvider =
  createVulnerabilitiesGridContextProvider(
    // @ts-ignore todo TS-3.8.3 - TS-8508
    ApplicationVulnerabiltiesGridContext,
    aggregationConfigs,
    'applications-vulns',
    'SEVERITY',
    orderFieldConfig,
    (routeParameters) => {
      const appID = getApplicationDrilldownID(routeParameters)
      return !!appID ? getQueries(appID) : undefined
    },
    (routeParameters) => {
      return (
        routeParameters.length >= 4 &&
        routeParameters[1] === 'applications' &&
        routeParameters[3].indexOf('vulns') > -1
      )
    },
    chartVisualizationConfig[0].id
  )

export function ApplicationVulnerabilitiesGrid() {
  const { aggregationSelections } = React.useContext(
    ApplicationVulnerabiltiesGridContext
  )
  const { parameters } = React.useContext(RouteContext)
  const organizationUuid = getApplicationOrgId(parameters)
  const applicationUuid = getApplicationDrilldownID(parameters)
  const { isLoading, data: appInfo } = useApplicationInfo({
    applicationUuid,
    organizationUuid
  })
  const { data: totalAppCustomAgentSession } =
    useTotalApplicationCustomAgentSessions({
      applicationUuid,
      organizationUuid
    })

  useSessionFiltersSync()

  if (!isLoading && !applicationUuid) {
    throw new Error('Unable to parse application ID from route')
  }

  return isLoading ? (
    <></>
  ) : (
    <VulnerabilitiesGridMain
      headerExtraChildren={(props) => {
        const {
          aggregationSelections,
          onOptionToggle,
          data: { sessionMetadataFields }
        } = props.gridContext

        return (
          <SessionMetadataAction
            appName={appInfo?.application?.name}
            applicationUuid={applicationUuid}
            organizationUuid={organizationUuid}
            filterFields={sessionMetadataFields}
            filtersActive={aggregationSelections}
            filterToggle={onOptionToggle}
            totalAppCustomAgentSession={totalAppCustomAgentSession}
          />
        )
      }}
      renderChartToggle={({ onClick }) => <ChartToggle onClick={onClick} />}
      renderChartContainer={({
        onCollapse,
        chartVisualization,
        onChartVisualizationToggle,
        severityChartSnapshots,
        discoveryChartSnapshots,
        sessionMetadataSelections,
        dateRangeOption,
        dateRange,
        loadingSnapshots
      }) => (
        <ChartContainer
          onCollapse={onCollapse}
          chartVisualizationConfig={chartVisualizationConfig}
          chartVisualization={chartVisualization}
          onChartVisualizationToggle={onChartVisualizationToggle}
          chart={renderChartSwitch({
            chartVisualization: chartVisualization as ChartVisualizationType,
            chartHeight,
            severityChartSnapshots,
            discoveryChartSnapshots,
            dateRangeOption,
            dateRange
          })}
          sessionMetadataSelections={sessionMetadataSelections}
          dateRangeOption={dateRangeOption}
          dateRange={dateRange}
          newVulnsCount={discoveryChartSnapshots.reduce(
            (acc, value) => acc + value.newlyIntroduced,
            0
          )}
          loadingSnapshots={loadingSnapshots}
        />
      )}
      chartContainerHeight={chartContainerHeight}
      isDrilldown
      applicationId={applicationUuid}
      tutorialKey={'APP_VULNERABILITIES'}
      renderBulkActions={getBulkActions<AggregationCategory>({
        aggregation: { applications: [applicationUuid || ''] }
      })}
      columns={({
        dateRange,
        dateRangeOption,
        onSetCustomDateRange,
        onSetDateRangeOption,
        onClearDateRange,
        dateField,
        onSetDateField,
        organizationID,
        organizationRole,
        features,
        hasLicenses,
        refetchQueries,
        refresh,
        trackJob,
        sessionMetadataFieldID,
        sessionMetadataAggregations,
        metadataSelections,
        onClearMetadataSelections,
        onMetadataOptionsToggle,
        restrictedEditRole,
        protectAssessEnv,
        isProtectColumnEnabled,
        isProtectEnabled
      }) =>
        getColumns({
          appID: applicationUuid,
          applicationInfo: appInfo?.application,
          organizationID,
          organizationRole,
          hasLicenses,
          dateRange,
          dateRangeOption,
          onSetCustomDateRange,
          onSetDateRangeOption,
          onClearDateRange,
          dateField,
          onSetDateField,
          features,
          refresh,
          refetchQueries,
          trackJob,
          sessionMetadataAggregations,
          sessionMetadataFieldID,
          metadataSelections,
          onClearMetadataSelections,
          onMetadataOptionsToggle,
          aggregationSelections,
          restrictedEditRole,
          protectAssessEnv,
          isProtectColumnEnabled,
          isProtectEnabled
        })
      }
    />
  )
}

export function getBulkActions<TAggregationCategory extends string>({
  aggregation,
  isServerGrid
}: {
  aggregation: { [key: string]: string[] }
  isServerGrid?: boolean
}) {
  return function renderBulkActions({
    rowSelectionProps,
    organizationID,
    organizationRole,
    refetchQueries,
    refresh,
    isFreemiumOrganization,
    trackJob,
    restrictedEditRole
  }: BulkActionProps<GQLVulnerabilityOrSinkGroup, TAggregationCategory>) {
    const { selectedCount, filters, additionalFilters } = rowSelectionProps
    const {
      deselectedVulns,
      selectedVulns,
      leastPermissiveApplicationRoleOfSelected
    } = filterVulnerabilities(rowSelectionProps)

    const mostPermissiveApplicationRole = isServerGrid
      ? getMostPermissiveRole(
          selectedVulns
            .concat(deselectedVulns)
            .map((vulnerability) => vulnerability.application.role)
        )
      : leastPermissiveApplicationRoleOfSelected

    return rowSelectionProps.invertedSelection && filters.quickFilter ? (
      <FilteredActionList
        organizationID={organizationID}
        refetchQueries={refetchQueries}
        refresh={refresh}
        selectedCount={selectedCount}
        vulnerabilities={selectedVulns}
        excludedVulnerabilities={deselectedVulns}
        filters={{
          ...filters,
          quickFilter: filters.quickFilter,
          aggregations: {
            ...filters.aggregations,
            ...aggregation
          }
        }}
        additionalFilters={additionalFilters}
        leastPermissiveApplicationRoleOfSelected={
          leastPermissiveApplicationRoleOfSelected
        }
        isFreemiumOrganization={isFreemiumOrganization}
        trackJob={trackJob}
        isAppsGrid={!isServerGrid}
      />
    ) : (
      <BulkActionList
        organizationID={organizationID}
        organizationRole={organizationRole}
        refetchQueries={refetchQueries}
        refresh={refresh}
        leastPermissiveApplicationRoleOfSelected={
          leastPermissiveApplicationRoleOfSelected
        }
        mostPermissiveApplicationRole={mostPermissiveApplicationRole}
        vulnerabilities={selectedVulns}
        isFreemiumOrganization={isFreemiumOrganization}
        trackJob={trackJob}
        restrictedEditRole={restrictedEditRole}
      />
    )
  }
}

/**
 * Exported for testing.
 *
 * Filters vulnerabilities from vulnerabilityOrSinkGroup to be used with bulk actions.
 * Bulk actions for sink groups will be implemented in TS-9346.
 */
export function filterVulnerabilities({
  selectedItems,
  deselectedItems
}: {
  selectedItems: GQLVulnerabilityOrSinkGroup[]
  deselectedItems: GQLVulnerabilityOrSinkGroup[]
}) {
  const deselectedVulns = deselectedItems
    .filter((vulnOrSinkGroup) => vulnOrSinkGroup.vulnerability)
    .map((withVuln) => withVuln.vulnerability) as GQLVulnerability[]

  const selectedVulns = selectedItems
    .filter((vulnOrSinkGroup) => vulnOrSinkGroup.vulnerability)
    .map((withVuln) => withVuln.vulnerability) as GQLVulnerability[]

  const leastPermissiveApplicationRoleOfSelected = getLeastPermissiveRole(
    selectedVulns.map((vulnerability) => vulnerability.application.role)
  )

  return {
    deselectedVulns,
    selectedVulns,
    leastPermissiveApplicationRoleOfSelected
  }
}

function getColumns({
  appID,
  applicationInfo,
  organizationID,
  organizationRole,
  hasLicenses,
  dateRange,
  dateRangeOption,
  onSetCustomDateRange,
  onSetDateRangeOption,
  onClearDateRange,
  dateField,
  onSetDateField,
  features,
  refresh,
  refetchQueries,
  trackJob,
  sessionMetadataAggregations,
  sessionMetadataFieldID,
  metadataSelections,
  onClearMetadataSelections,
  onMetadataOptionsToggle,
  aggregationSelections,
  restrictedEditRole,
  protectAssessEnv,
  isProtectColumnEnabled,
  isProtectEnabled
}): Array<Column<VulnerabilityOrSinkGroup, AggregationCategory>> {
  return [
    getSeverityColumn<AggregationCategory>({
      aggregations: ['severities'],
      organizationID,
      refetchQueries,
      refresh
    }),
    getVulnerabilityColumn<AggregationCategory>({
      aggregations: [
        'tags',
        'bugtrackerStatuses',
        'types',
        'modules',
        'buildNumbers',
        'servers',
        'environments',
        'sinks',
        'urls',
        'securityStandards',
        'routes'
      ],
      organizationID,
      organizationRole,
      dateField,
      hasLicenses,
      features,
      refresh,
      refetchQueries,
      getDrilldownPath: (id) =>
        getApplicationVulnerabilityDrilldownRoute(organizationID, appID, id)
    }),
    getApplicationColumn<AggregationCategory>({
      organizationID,
      organizationRole,
      hasLicenses,
      features,
      // Don't make the application name a link because all of the names
      // should be for the application that we're already viewing
      renderLink: false,
      aggregations: ['modules'],
      applicationInfo
    }),
    getProtectStatusColumn<AggregationCategory>({
      aggregations: ['protectStatuses' as AggregationCategory],
      protectAssessEnv,
      isProtectColumnEnabled,
      isProtectEnabled
    }),
    getDateDetectedColumn<AggregationCategory>({
      dateRange,
      dateRangeOption,
      onSetCustomDateRange,
      onSetDateRangeOption,
      onClearDateRange,
      dateField,
      onSetDateField
    }),
    getStatusColumn<AggregationCategory>({
      organizationID,
      refetchQueries,
      refresh,
      aggregations: ['statuses', 'substatus']
    }),
    getSessionMetadataColumn<AggregationCategory>({
      sessionMetadataAggregations,
      sessionMetadataFieldID,
      metadataSelections,
      onClearMetadataSelections,
      onMetadataOptionsToggle,
      aggregationSelections
    }),
    getRowActionColumn({
      organizationID,
      organizationRole,
      refresh,
      refetchQueries,
      trackJob,
      restrictedEditRole
    })
  ]
}
